import React, { useState, useMemo } from 'react';
import { useSettings } from '../lib/usePublicData';
import { ChevronDown, Sparkles, X } from 'lucide-react';
import VipRequestForm from './VipRequestForm';

/**
 * Crazy VIP Ervaring — publiek consumenten-aanbod.
 * Toont:
 * - Bungee titel + datum/locatie/uur
 * - €75 / persoon — excl. BTW (toggle-baar via vip_show_price_public)
 * - Inclusief-bullets (uit settings.vip_inclusions, één per regel)
 * - Expandable menu (uit settings.vip_menu_text)
 * - CTA "Aanvraag Crazy VIP" → modal (desktop) / sheet (mobiel)
 *
 * Verbergt zichzelf volledig als settings.vip_enabled !== true.
 */
const CrazyVipExperience = () => {
    const { data: settings } = useSettings();
    const [menuOpen, setMenuOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);

    const inclusions = useMemo(() => {
        const raw = (settings?.vip_inclusions || '').split('\n').map((l) => l.trim()).filter(Boolean);
        return raw;
    }, [settings?.vip_inclusions]);

    if (!settings || !settings.vip_enabled) return null;

    return (
        <div data-testid="crazy-vip-experience" className="mt-8">
            <article
                className="relative rounded-3xl border-4 border-[var(--green)] bg-[var(--cream)] p-6 sm:p-10 overflow-hidden shadow-[0_8px_0_var(--green-dark)]"
            >
                {/* Decoratieve pastel-vlek */}
                <div aria-hidden="true" className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[var(--pink)]/40 blur-2xl pointer-events-none" />
                <div aria-hidden="true" className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-[var(--yellow)]/40 blur-3xl pointer-events-none" />

                {/* Premium badge */}
                <div className="relative inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--green)] text-[var(--cream)] text-[10px] font-display tracking-wider mb-4" data-testid="vip-premium-badge">
                    <Sparkles size={12} aria-hidden="true" /> EXCLUSIEF VIP-AANBOD
                </div>

                <h3
                    className="font-display text-[var(--green-dark)] leading-tight mb-3"
                    style={{ fontSize: 'clamp(1.75rem, 5vw, 3rem)' }}
                    data-testid="vip-title"
                >
                    {settings.vip_title || 'Crazy VIP Ervaring'}
                </h3>

                <div className="flex flex-wrap items-center gap-2 mb-5 text-xs sm:text-sm font-semibold text-[var(--green-dark)]">
                    <span className="zz-pill" data-testid="vip-date">{settings.vip_date_text || '29 augustus 2026'}</span>
                    <span className="zz-pill">{settings.vip_location || 'Grote Markt'}</span>
                    <span className="zz-pill">{settings.vip_time || 'Aanvang 18u00'}</span>
                </div>

                {settings.vip_show_price_public && (
                    <div
                        data-testid="vip-price"
                        className="inline-block font-display text-[var(--green)] mb-6"
                        style={{
                            fontSize: 'clamp(1.5rem, 4vw, 2.25rem)',
                            backgroundColor: 'var(--yellow)',
                            padding: '8px 20px',
                            borderRadius: '14px',
                            border: '3px solid var(--green)',
                            boxShadow: '0 3px 0 var(--green-dark)',
                            transform: 'rotate(-1.5deg)',
                        }}
                    >
                        {settings.vip_price_label || '€75 / persoon — excl. BTW'}
                    </div>
                )}

                {/* Inclusief lijst */}
                {inclusions.length > 0 && (
                    <div className="mb-6">
                        <h4 className="font-display text-sm tracking-wider text-[var(--green-dark)] mb-3">INCLUSIEF</h4>
                        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[var(--green-dark)]" data-testid="vip-inclusions-list">
                            {inclusions.map((inc, i) => (
                                <li key={i} className="flex items-start gap-2">
                                    <span aria-hidden="true" className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-[var(--green)] flex-shrink-0" />
                                    <span>{inc}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Expandable menu */}
                {settings.vip_menu_text && (
                    <div className="mb-6">
                        <button
                            type="button"
                            onClick={() => setMenuOpen((o) => !o)}
                            data-testid="vip-menu-toggle"
                            aria-expanded={menuOpen}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--green-dark)] hover:text-[var(--green)] underline underline-offset-2"
                        >
                            {menuOpen ? 'Verberg volledig menu' : 'Bekijk volledig menu'}
                            <ChevronDown size={16} className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                        </button>
                        {menuOpen && (
                            <pre
                                data-testid="vip-menu-text"
                                className="mt-3 whitespace-pre-wrap font-body text-sm text-[var(--green-dark)] bg-white/70 rounded-2xl p-4 border border-[var(--green)]/30 leading-relaxed"
                                style={{ fontFamily: "'Inter', sans-serif" }}
                            >
                                {settings.vip_menu_text}
                            </pre>
                        )}
                    </div>
                )}

                {settings.vip_request_enabled !== false && (
                    <button
                        type="button"
                        onClick={() => setFormOpen(true)}
                        data-testid="vip-cta-open-form"
                        className="zz-btn zz-btn--primary font-display tracking-wider"
                        style={{ padding: '14px 22px', fontSize: '13px' }}
                    >
                        AANVRAAG CRAZY VIP →
                    </button>
                )}
            </article>

            {formOpen && (
                <VipFormDialog onClose={() => setFormOpen(false)} settings={settings} />
            )}
        </div>
    );
};

const VipFormDialog = ({ onClose, settings }) => {
    return (
        <div
            data-testid="vip-form-dialog"
            className="fixed inset-0 z-[60] bg-black/50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="relative bg-cream w-full sm:max-w-2xl sm:rounded-3xl sm:border-2 sm:border-[var(--green)] shadow-2xl flex flex-col max-h-screen sm:max-h-[92vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="sticky top-0 bg-cream border-b-2 border-[var(--green)] px-5 sm:px-7 py-4 flex items-center justify-between gap-3 z-10">
                    <h3 className="font-display text-base sm:text-lg text-[var(--green-dark)]">{settings.vip_title || 'Crazy VIP Ervaring'} — aanvraag</h3>
                    <button onClick={onClose} aria-label="Sluiten" data-testid="vip-form-close" className="text-[var(--green-dark)] hover:text-[var(--green)] p-1">
                        <X size={20} />
                    </button>
                </header>
                <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5">
                    <VipRequestForm settings={settings} onClose={onClose} />
                </div>
            </div>
        </div>
    );
};

export default CrazyVipExperience;
